import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
  inject,
} from '@angular/core';
import {
  ImageFallbacks,
  ImagePlaceholders,
  NomConnectorObjectType,
  NomObjectClass,
  NomObjectLifecycleStatus,
  NomObjectTypes,
} from '../../../constants';
import {
  getIconForDataObject,
  getImageForDataObject,
  getTitleForDataObject,
} from 'src/app/shared/utils/object-utils';

import { AppState } from 'src/app/reducers';
import { DataObject } from '../../../models/core.models';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ObjectActionsService } from 'src/app/core/services/object-actions.service';
import { ObjectCardDropdownComponent } from './object-card-dropdown/object-card-dropdown.component';
import { ObjectEditorService } from '../../../services/object-editor.service';
import { Principal } from 'src/app/core/services/object.service';
import { PropertyViewConfig } from 'src/app/core/services/feed-settings.service';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';

interface ObjectTypeConfig {
  title: string;
  icon?: IconProp;
  image?: string;
}
@Component({
  selector: 'app-object-card',
  templateUrl: './object-card.component.html',
  styleUrls: ['./object-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ObjectCardComponent implements OnInit, OnChanges {
  @ViewChild('cardDropdownContainer', { read: ViewContainerRef })
  container: ViewContainerRef;

  @Input() object: DataObject;
  @Input() selectMode: boolean = false;
  @Input() selected: boolean = false;
  @Input() selectable: boolean = true;
  @Input() filterString: string = '';
  @Input() viewConfig: PropertyViewConfig[] = [];
  @Input() objectActionService: ObjectActionsService;
  @Input() titleCharLimit: number = 60;
  @Input() descriptionCharLimit: number = 30;

  @Output() onObjectSelect: EventEmitter<DataObject> = new EventEmitter();
  @Output() onEdit: EventEmitter<DataObject> = new EventEmitter();
  @Output() onClick: EventEmitter<DataObject> = new EventEmitter();

  NomObjectClass = NomObjectClass;
  canEdit: boolean = false;
  hasMenu: boolean = true;
  hasGoTto: boolean = true;

  className: string;
  objectConfig: ObjectTypeConfig;
  imageSrc: string;
  objectTitle: string = 'Object';
  objectIcon: IconProp;
  timeCreatedVisible: boolean;
  timeModifiedVisible: boolean;
  objectDescription: string = '';
  sharedWithTooltip: string = '';
  statsVisible: boolean;

  readonly loadingImage = ImagePlaceholders.spinner;
  readonly avatarFallback = ImageFallbacks.avatar;

  static readonly feedTypeClassMap = new Map<NomObjectTypes, string>([
    [NomObjectTypes.CLIPPING_FEED, 'clipping-feed'],
    [NomObjectTypes.COMBINED_FEED, 'combined-feed'],
    [NomObjectTypes.ONLINE_FEED, 'online-feed'],
    [NomObjectTypes.SOCIAL_FEED, 'social-feed'],
    [NomObjectTypes.BROADCAST_FEED, 'broadcast-feed'],
    [NomObjectTypes.STORY_FEED, 'story-feed'],
  ]);

  static readonly connectorTypeClassMap = new Map<
    NomConnectorObjectType,
    string
  >([
    [NomConnectorObjectType.FACEBOOK_INSIGHTS, 'facebook'],
    [NomConnectorObjectType.GOOGLE_ANALYTICS, 'google-analytics'],
    [NomConnectorObjectType.TWITTER, 'twitter'],
    [NomConnectorObjectType.TELEGRAM, 'telegram'],
    [NomConnectorObjectType.INSTAGRAM, 'instagram'],
    [NomConnectorObjectType.TIKTOK, 'tiktok'],
    [NomConnectorObjectType.YOUTUBE, 'youtube'],
    [NomConnectorObjectType.FACEBOOK, 'facebook'],
  ]);

  // Define a mapping for class IDs to class names
  private static classMap = new Map([
    [NomObjectClass.WORKSPACE, 'workspace'],
    [NomObjectClass.DASHBOARD, 'dashboard'],
    [NomObjectClass.FEED, 'feed'], // Default for feed; will check object_type_id later if needed
    [NomObjectClass.EXPORT_SCHEDULE, 'schedule'],
    [NomObjectClass.ALERT, 'alert'],
    [NomObjectClass.CONNECTOR, 'connector'],
  ]);

  private static lifecycleMap = new Map([
    [NomObjectLifecycleStatus.ARCHIVED, 'archived'],
    [NomObjectLifecycleStatus.DELETED, 'deleted'],
  ]);

  // Optionally, define a separate map for special cases within FEED class
  private static feedTypeMap = new Map([
    [NomObjectTypes.SEARCH_DIMENSION, 'dimension'],
    // Add more special cases for FEED types if needed
  ]);

  private clickInProgress = false;

  protected destroyRef = inject(DestroyRef);

  isVisible = false;
  private observer: IntersectionObserver | null = null;

  constructor(
    private router: Router,
    private store: Store<AppState>,
    private editService: ObjectEditorService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef,
  ) {}

  private getObjectIcon(object: DataObject) {
    return getIconForDataObject(object.object_class_id, object.object_type_id);
  }

  private getObjectImage(object: DataObject) {
    return getImageForDataObject(object.object_class_id, object.object_type_id);
  }

  private getObjectTitle(object: DataObject) {
    return getTitleForDataObject(object.object_class_id, object.object_type_id);
  }

  determineObjectTypeConfig(object: DataObject): ObjectTypeConfig {
    const title = this.getObjectTitle(object);
    const icon = this.getObjectIcon(object);
    const image = this.getObjectImage(object);

    return {
      title,
      icon,
      image,
    };
  }

  ngOnInit(): void {
    // Your @Input() property is available
    this.determineClassName();
    this.objectConfig = this.determineObjectTypeConfig(this.object);
    if (this.object.description) {
      this.objectDescription = this.truncateString(
        this.object.description,
        this.descriptionCharLimit,
      );
    }

    // Simplify checking the `can_edit` property
    this.canEdit = this.object.can_edit !== false;

    // Use an array to check if the `object_class_id` matches any of the specified classes for `hasMenu`
    const menuObjectClasses = [
      NomObjectClass.FEED,
      NomObjectClass.DASHBOARD,
      NomObjectClass.WORKSPACE,
      // NomObjectClass.EXPORT_SCHEDULE,
      // NomObjectClass.ALERT,
    ];
    this.hasMenu = menuObjectClasses.includes(this.object.object_class_id);

    // Since `hasGoTto` includes all the classes in `hasMenu` plus `NomObjectClass.CONNECTOR`,
    // add that to the array and use the same approach
    const gotoObjectClasses = [...menuObjectClasses, NomObjectClass.CONNECTOR];
    this.hasGoTto = gotoObjectClasses.includes(this.object.object_class_id);
    this.sharedWithTooltip = this.getSharedWithTitles(this.object.shared_with);
  }

  ngAfterViewInit() {
    this.observer = new IntersectionObserver(
      (entries) => {
        if ((entries[0] as any).isIntersecting) {
          this.isVisible = true;
          this.cdr.markForCheck();
          this.observer?.disconnect(); // Stop watching once it's loaded
        }
      },
      {
        root: null, // observing for viewport
        threshold: 0.1, // percentage of target's visibility the observer's callback should be executed
      },
    );

    this.observer.observe(this.el.nativeElement);
  }

  ngOnDestroy() {
    this.observer?.disconnect();
  }

  ngOnChanges(changes) {
    if (changes['viewConfig']) {
      // update visibility flags
      const created = this.viewConfig.find(
        (item) => item.path === 'time_created',
      );
      const modified = this.viewConfig.find(
        (item) => item.path === 'time_modified',
      );
      const stats = this.viewConfig.find((item) => item.path === 'stats');
      this.timeCreatedVisible = created?.selected ?? false;
      this.timeModifiedVisible = modified?.selected ?? false;
      this.statsVisible = stats?.selected ?? false;
    }
  }

  toggleSelect() {
    if (this.selectable === false) {
      return;
    }
    if (this.object.can_edit === false) {
      return;
    }
    if (this.objectActionService) {
      this.objectActionService.toggleObjectSelection(this.object);
      this.selected = this.objectActionService.isObjectSelected(this.object);
    } else {
      this.selected = !this.selected;
    }
    this.onObjectSelect.emit(this.object);
  }

  determineClassName(): void {
    const { object_class_id, object_type_id, lifecycle_status } = this.object;

    // Base class name lookup
    let className =
      ObjectCardComponent.classMap.get(object_class_id) || 'default';

    // Special case for FEED class
    if (object_class_id === NomObjectClass.FEED) {
      className =
        ObjectCardComponent.feedTypeMap.get(object_type_id) || className;
    }

    // Append lifecycle status if available
    const lifecycleClass =
      ObjectCardComponent.lifecycleMap.get(lifecycle_status);
    if (lifecycleClass) {
      className += ` ${lifecycleClass}`;
    }

    // Apply feed-specific class names
    if (className.includes('feed')) {
      const feedClass =
        ObjectCardComponent.feedTypeClassMap.get(object_type_id);
      if (feedClass) {
        className = feedClass;
      }
    }

    // Append connector-specific class names
    if (className.includes('connector')) {
      const connectorClass =
        ObjectCardComponent.connectorTypeClassMap.get(object_type_id);
      if (connectorClass) {
        className += ` ${connectorClass}`;
      }
    }

    this.className = className;
  }

  showDropdown(event: MouseEvent): void {
    event.stopPropagation();
    setTimeout(() => {
      if (this.container.length > 0) {
        // Dropdown is already opened → close it
        this.container.clear();
        return;
      }
      this.container.clear(); // Clear the container in case it's already populated
      const componentRef = this.container.createComponent(
        ObjectCardDropdownComponent,
      );
      // Now set the input properties on the component instance
      componentRef.instance.objectId = this.object.id;
      componentRef.instance.objectClassId = this.object.object_class_id;
      componentRef.instance.objectTypeId = this.object.object_type_id;
      componentRef.instance.lifecycleStatus = this.object.lifecycle_status;
      componentRef.instance.objectTitle = this.object.title;
      componentRef.instance.ngOnInit();
    }, 1);
  }

  handleClick(ignoreSelectMode: boolean = false) {
    if (this.clickInProgress) {
      return;
    }

    this.clickInProgress = true;

    try {
      switch (this.object.lifecycle_status) {
        case NomObjectLifecycleStatus.ARCHIVED:
        case NomObjectLifecycleStatus.DELETED:
          return;
      }

      if (this.selectMode && ignoreSelectMode === false) {
        this.toggleSelect();
      } else {
        switch (this.object.object_class_id) {
          case NomObjectClass.WORKSPACE:
            this.router.navigate(['analytics', 'workspace', this.object.id]);
            break;

          case NomObjectClass.DASHBOARD:
            this.goToDashboard(this.object.workspace_id, this.object.id);
            break;

          case NomObjectClass.FEED:
            switch (this.object.object_type_id) {
              case NomObjectTypes.SEARCH_DIMENSION:
                this.editService.loadFeedAndOpenPopup(this.object.id);
                break;
              default:
                this.goToFeed(this.object.workspace_id, this.object.id);
                break;
            }
            break;

          case NomObjectClass.CONNECTOR:
            this.gotoConnector(this.object);
            break;
        }

        this.onClick.emit(this.object);
      }
    } finally {
      // release after a short delay (important!)
      setTimeout(() => {
        this.clickInProgress = false;
      }, 500);
    }
  }

  onObjectEdit(event: MouseEvent) {
    event.stopPropagation();

    switch (this.object.lifecycle_status) {
      case NomObjectLifecycleStatus.ARCHIVED:
      case NomObjectLifecycleStatus.DELETED:
        return;
    }
    // Assuming this is within a method of your component class
    if (this.onEdit.observers.length > 0) {
      this.onEdit.emit(this.object);
    } else {
      switch (this.object.object_class_id) {
        case NomObjectClass.WORKSPACE:
          this.editService.loadWorkspaceAndOpenPopup(this.object.id);
          break;
        case NomObjectClass.DASHBOARD:
          this.editService.loadDashboardAndOpenPopup(this.object.id);
          break;
        case NomObjectClass.FEED:
          this.checkFeedAndOpenPopup(this.object.id);
          break;
        default:
          // Handle any other cases or do nothing
          break;
      }
    }
  }

  /**
   * Navigates to the specified feed within a workspace.
   *
   * @param workspaceId - The ID of the workspace.
   * @param feedId - The ID of the feed.
   */
  goToFeed(workspaceId: string, feedId: string): void {
    this.router.navigate([
      'analytics',
      'workspace',
      workspaceId,
      'feed',
      feedId,
    ]);
  }

  /**
   * Navigates to the connector page with the specified connector ID.
   *
   * @param connector - The DataObject representing the connector.
   */
  gotoConnector(connector: DataObject) {
    this.router.navigate([
      'connector-manager',
      connector.id,
      'rules',
      'enabled',
      'active',
    ]);
  }

  /**
   * Navigates to the dashboard with the specified workspace ID and dashboard ID.
   *
   * @param workspaceId - The ID of the workspace.
   * @param dashboardId - The ID of the dashboard.
   */
  goToDashboard(workspaceId: string, dashboardId: string): void {
    this.router.navigate([
      'analytics',
      'workspace',
      workspaceId,
      'dashboard',
      dashboardId,
    ]);
  }

  checkFeedAndOpenPopup(feedId: string) {
    this.editService.loadFeedAndOpenPopup(feedId);
  }

  truncateString(str: string, limit: number = 40): string {
    if (str.length > limit) {
      return str.slice(0, limit) + '...';
    } else {
      return str;
    }
  }

  // object-card.component.ts
  getSharedWithTitles(sharedWith: Array<Principal>): string {
    return (sharedWith ?? [])
      .map((o) => (typeof o === 'string' ? o : o.name || o.email))
      .join(', ');
  }
}
